// El agente a pantalla completa, después de elegir los productos
// (ADR-2026-10-14-el-catalogo-arranca-con-el-agente-a-pantalla-completa): Cumbre le pregunta a
// la persona cómo quiere el catálogo, con las miniaturas de algunos de los productos elegidos,
// la caja (voz, referencias, planillas) y, debajo, UNA sugerencia grande: "Dejáselo al agente". La misma forma que la
// bienvenida del agente (`InicioAgente`): la mascota a la izquierda y la burbuja a su derecha.
// Al mandar, la sesión se abre con la conversación a pantalla completa
// (ADR-2026-10-14-el-catalogo-conversa-a-pantalla-completa-hasta-armar). Mientras está, la
// sidebar va a la tira (`useTiraDeSidebar`).
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import CachedImg from '../../../../components/CachedImg';
import { MascotaConSaludo } from '../../../../chat-atlas/ui/MascotaAgente';
import type { ChatAttachment } from '../../agentAttachments';
import { AnimatedBlurBg } from '../../AnimatedBlurBg';
import type { MockProductV2 } from '../../mockData';
import { CajaDeCatalogo } from './CajaDeCatalogo';
import { useDebajoDeMd } from '../../../../hooks/useEsMobile';
import { estiloDelMarcoTelefono, MARCO_TELEFONO } from './marcoDelTelefono';
import { useTiraDeSidebar } from './useTiraDeSidebar';

export const PREGUNTA_DEL_AGENTE = 'Explicame cómo querés tu catálogo';
/** El pedido de "Dejáselo al agente" (ADR-2026-10-14-una-sugerencia-grande-dejaselo-al-agente):
 *  el agente decide todo a partir de los productos. */
export const PEDIDO_DEJARSELO_AL_AGENTE =
  'Dejámelo a vos: mirá los productos que elegí y proponeme un catálogo para ellos. Decidí vos las tomas, el estilo y el formato que mejor les queden.';
/** Cuántas miniaturas se muestran; el resto va como "+N". */
const MINIATURAS = 5;

export interface IntroDelCatalogoProps {
  productos: MockProductV2[];
  enviando: boolean;
  error: string | null;
  onEnviar: (texto: string, adjuntos: ChatAttachment[]) => void;
  onVolver: () => void;
}

export const IntroDelCatalogo: React.FC<IntroDelCatalogoProps> = ({ productos, enviando, error, onEnviar, onVolver }) => {
  const corrimiento = useTiraDeSidebar(true);
  const esTelefono = useDebajoDeMd();
  const [valor, setValor] = useState('');
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  const conFoto = productos.filter((p) => p.imageUrl);
  const vistos = conFoto.slice(0, MINIATURAS);
  const resto = productos.length - vistos.length;

  return (
    <div className={`flex h-full flex-col overflow-y-auto ${MARCO_TELEFONO}`} style={estiloDelMarcoTelefono(esTelefono)}>
      {/* Con la tira, el logo flota arriba a la izquierda: el botón se corre para no quedar debajo.
          En el teléfono no va: el botón de atrás del shell vuelve a los productos
          (ADR-2026-10-15-el-catalogo-en-el-telefono-3). */}
      <div className="hidden shrink-0 pr-6 pt-5 md:block" style={{ paddingLeft: corrimiento + 24 }}>
        <button
          type="button"
          onClick={onVolver}
          disabled={enviando}
          className="inline-flex items-center gap-1.5 rounded-full py-1.5 px-3 text-[13.5px] font-semibold text-[#3C3C43]/70 transition-colors hover:bg-black/[0.04] hover:text-brand-black disabled:opacity-40"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          Cambiar productos
        </button>
      </div>
      <div className="mx-auto flex w-full max-w-[760px] flex-1 flex-col items-center justify-center gap-6 px-4 pb-8 sm:gap-8 sm:px-6 sm:pb-10">
        {/* Las miniaturas de lo elegido: dicen de qué catálogo se está hablando. */}
        <div className="flex items-center justify-center -space-x-3" aria-label={`${productos.length} producto${productos.length === 1 ? '' : 's'} elegido${productos.length === 1 ? '' : 's'}`}>
          {vistos.map((p, i) => (
            <span
              key={p.id}
              className="animate-fade-in block h-[76px] w-[76px] overflow-hidden rounded-2xl bg-white shadow-md ring-[3px] ring-white"
              style={{
                transform: `rotate(${((i / Math.max(1, vistos.length - 1)) * 2 - 1) * 8}deg)`,
                animationDelay: `${i * 60}ms`,
                zIndex: vistos.length - Math.abs(i - (vistos.length - 1) / 2),
              }}
            >
              <CachedImg src={p.imageUrl as string} alt="" className="h-full w-full object-cover" />
            </span>
          ))}
          {resto > 0 && (
            <span className="relative grid h-[52px] min-w-[52px] place-items-center rounded-2xl bg-[rgb(var(--brand-primary-rgb)_/_0.095)] px-3 text-[14px] font-semibold text-brand-primary ring-[3px] ring-white">
              +{resto}
            </span>
          )}
        </div>

        {/* Cumbre pregunta: la forma de un mensaje del agente con su avatar. */}
        <div className="flex w-full items-end justify-center gap-3 sm:gap-5">
          <MascotaConSaludo size={120} burbuja="arriba" saludo={false} className="h-auto w-[72px] shrink-0 sm:w-[96px]" />
          <p className="min-w-0 rounded-[18px] rounded-tl-[6px] bg-[#F2F2F7] px-4 py-3 text-left text-[17px] font-semibold sm:px-5 sm:py-4 sm:text-[20px] leading-snug text-brand-black">
            {PREGUNTA_DEL_AGENTE}
          </p>
        </div>

        <div className="flex w-full flex-col gap-3">
          <CajaDeCatalogo
            valor={valor}
            onCambiarValor={setValor}
            adjuntos={adjuntos}
            onAgregarAdjunto={(a) => setAdjuntos((prev) => [...prev, a])}
            onQuitarAdjunto={(id) => setAdjuntos((prev) => prev.filter((a) => a.id !== id))}
            onEnviar={(t) => onEnviar(t, adjuntos)}
            placeholder="Qué tomas, qué estilo, para dónde es (tienda, PDF, redes)…"
            bloqueado={enviando}
            motivoBloqueo="Abriendo el catálogo…"
            alto="amplio"
            autoFoco
          />
          {/* UNA sugerencia grande en vez de varias chicas: dejarle todo al agente. Manda el
              pedido en el acto (con las referencias que haya en la caja). */}
          <button
            type="button"
            onClick={() => onEnviar(PEDIDO_DEJARSELO_AL_AGENTE, adjuntos)}
            disabled={enviando}
            className="group relative mx-auto mt-6 inline-flex max-w-[480px] items-center gap-3 overflow-hidden rounded-2xl bg-[#F5F7FE] py-2.5 pl-2.5 pr-4 text-left ring-1 ring-[rgb(var(--brand-primary-rgb)_/_0.12)] transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-[0_8px_24px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] active:scale-[0.99] disabled:cursor-default disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {/* El blur en movimiento, apenas: es un fondo, no el protagonista. */}
            <span className="pointer-events-none absolute inset-0 opacity-[0.16]" aria-hidden="true">
              <AnimatedBlurBg selected />
            </span>
            <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-brand-primary shadow-sm">
              <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            </span>
            <span className="relative min-w-0 flex-1">
              <span className="block text-[14.5px] font-semibold leading-tight text-brand-black">Dejáselo al asistente</span>
              <span className="mt-0.5 block text-[12.5px] leading-snug text-[#3C3C43]/65">
                Mira {productos.length === 1 ? 'el producto' : `los ${productos.length} productos`} y te propone un catálogo.
              </span>
            </span>
            <ArrowRight className="relative h-4 w-4 shrink-0 text-brand-primary transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={2.25} aria-hidden="true" />
          </button>
          {enviando && (
            <p className="flex items-center justify-center gap-2 text-[13px] text-[#3C3C43]/60">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Abriendo el catálogo…
            </p>
          )}
          {error && <p className="text-center text-[13px] text-[#B42318]">{error}</p>}
        </div>
      </div>
    </div>
  );
};
